import { useEffect, useRef, useState } from 'react'
import { FilterMatchMode } from 'primereact/api'
import { OverlayPanel } from 'primereact/overlaypanel'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

// local imports
import { CampusServiceTypes, ICampusServiceReservations } from 'types/TrackingType'
import { saveAsExcelFile } from 'utils/Miscellaneous'

export default function useReservationTable(
  data: ICampusServiceReservations[] | undefined,
  selectedService: CampusServiceTypes[]
) {
  const { t } = useTranslation()

  /* States - Variables */
  const [activeTableData, setActiveTableData] = useState<ICampusServiceReservations[] | undefined>([])
  const [canceledTableData, setCanceledTableData] = useState<ICampusServiceReservations[] | undefined>([])
  const [filters, setFilters] = useState<any>(null)
  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [notification, setNotification] = useState(false)
  const [multiNotification, setMultiNotification] = useState(false)
  const [selectedRow, setSelectedRow] = useState<ICampusServiceReservations | undefined>()
  const [activeBlockList, setActiveBlockList] = useState<any>()
  const [activeFloorList, setActiveFloorList] = useState<any>()
  const [canceledBlockList, setCanceledBlockList] = useState<any>()
  const [canceledFloorList, setCanceledFloorList] = useState<any>()
  const addToExcel = useRef<OverlayPanel>(null)

  /* initiate filters */
  useEffect(() => {
    const initFilters = () => {
      setFilters({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        reservationStatusId: { value: null, matchMode: FilterMatchMode.EQUALS },
        blockName: { value: null, matchMode: FilterMatchMode.IN },
        floorName: { value: null, matchMode: FilterMatchMode.IN },
      })
    }
    initFilters()
  }, [])

  /* Manipulate response data and set states => (active - canceled reservations) */
  const handleData = () => {
    const selecteTypesOnly = data?.filter((rd) => selectedService.includes(rd.campusServiceType))
    const canceledData = selecteTypesOnly?.filter((datum) => datum.reservationStatusId === 3)
    const activeData = selecteTypesOnly?.filter((datum) => datum.reservationStatusId !== 3)
    setActiveTableData(activeData)
    setCanceledTableData(canceledData)

    const _activeBlockList = activeData
      ?.map((elem: any) => ({ name: elem.blockName, value: elem.blockId }))
      .filter((obj: any, pos: any, arr: any[]) => {
        return arr.map((mapObj) => mapObj.name).indexOf(obj.name) === pos
      })
    const _activeFloorList = activeData
      ?.map((elem: any) => ({ name: elem.floorName, value: elem.floorId }))
      .filter((obj: any, pos: any, arr: any[]) => {
        return arr.map((mapObj) => mapObj.name).indexOf(obj.name) === pos
      })

    setActiveBlockList(_activeBlockList)
    setActiveFloorList(_activeFloorList)

    const _canceledBlockList = canceledData
      ?.map((elem: any) => ({ name: elem.blockName, value: elem.blockId }))
      .filter((obj: any, pos: any, arr: any[]) => {
        return arr.map((mapObj) => mapObj.name).indexOf(obj.name) === pos
      })
    const _canceledFloorList = canceledData
      ?.map((elem: any) => ({ name: elem.floorName, value: elem.floorId }))
      .filter((obj: any, pos: any, arr: any[]) => {
        return arr.map((mapObj) => mapObj.name).indexOf(obj.name) === pos
      })

    setCanceledBlockList(_canceledBlockList)
    setCanceledFloorList(_canceledFloorList)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => handleData(), [selectedService, data])

  /* ################ Tabs  ################*/
  const [tabIndex, setTabIndex] = useState(0)
  const handleTabChange = (e: any) => setTabIndex(e.index)
  const tabs = [
    { key: 'active', header: t('visitor.active-reservations') },
    { key: 'canceled', header: t('visitor.canceled-reservations') },
  ]
  const isCameOptions = [
    { label: t('visitor.came'), value: 2 },
    { label: t('visitor.not-come'), value: 1 },
  ]
  const whichTabData = tabIndex === 0 ? activeTableData : canceledTableData
  const hasItems = !!whichTabData && whichTabData.length > 0

  /* ############# Excel Export ############# */
  const exportExcel = () => {
    import('xlsx').then((xlsx) => {
      const Heading = [
        [
          `${t('visitor.name')} ${t('visitor.surname')}`,
          t('visitor.email'),
          t('visitor.employee-firm'),
          t('visitor.location-block'),
          t('visitor.location-floor'),
          t('input-texts.date'),
          t('visitor.appointment-time'),
          t('visitor.res-reason'),
          t('visitor.res-description'),
          t('visitor.res-place'),
          t('visitor.res-person'),
          t('visitor.came'),
          ...(tabIndex === 1 ? [t('input-texts.cancel-date')] : []),
        ],
      ]

      const excData = tabIndex === 0 ? activeTableData : canceledTableData
      const fileName = tabIndex === 0 ? t('visitor.active-reservations') : t('visitor.canceled-reservations')
      const lookup = { 1: 'visitor.doctor', 2: 'visitor.coiffeur', 3: 'visitor.gym', 4: 'visitor.lactation' }

      /** TODO: follow issues
       * Global filtered values are not accessible in the datatable, currently there is no callback invoked when a global filter is applied.
       * => https://github.com/primefaces/primereact/issues/1763
       * => https://github.com/primefaces/primereact/issues/1763
       */
      const excelData = excData?.map((res) => {
        let temp = {
          EmployeeName: `${res.employeeName} ${res.employeeSurname}`,
          EmployeeEmail: res.employeeEmail,
          FirmName: res.firmName,
          BlockName: res.blockName,
          FloorName: res.floorName,
          Date: moment(res.reservationStartHour).format('DD/MM/YYYY'),
          Time: `${moment(res.reservationStartHour).format('HH:mm')} - ${moment(res.reservationEndHour).format(
            'HH:mm'
          )}`,
          ResReason: t(lookup[res.campusServiceType]) ?? '---',
          ResDesc:
            res.campusServiceSubType === 1
              ? t('visitor.personal-work')
              : res.campusServiceSubType === 2
              ? `${t('visitor.special-lesson')} (${res.servicePersonName} ${res?.servicePersonSurname})`
              : res.campusServiceSubType === 3
              ? t('visitor.group-lesson')
              : '---',
          ResPlace: res.placeOfServiceName ?? '---',
          ResPerson: `${res.servicePersonName ?? '---'} ${res.servicePersonSurname ?? ''}`,
          ReservationStatusId: res.reservationStatusId === 2 ? t('visitor.came') : t('visitor.not-come'),
          ...(tabIndex === 1 ? { CancelDate: moment(res.statusUpdateDate).format('DD/MM/YYYY HH:mm:ss') } : {}),
        }
        return temp
      })

      const ws = xlsx.utils.book_new()
      xlsx.utils.sheet_add_aoa(ws, Heading)
      const worksheet = xlsx.utils.sheet_add_json(ws, excelData!, { origin: 'A2', skipHeader: true })
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' })
      saveAsExcelFile(excelBuffer, fileName)
    })
  }
  const showExcel = (e: React.MouseEvent<HTMLDivElement>) => addToExcel.current?.show(e, e.target)
  /* ############# Excel Export ############# */

  /* Global table search (name, surname) */
  const onGlobalFilterChange = (input: any) => {
    const value = input
    let _filters = { ...filters }
    _filters['global'].value = value
    setFilters(_filters)
    setGlobalFilterValue(value)
  }

  return {
    activeTableData,
    canceledTableData,
    t,
    filters,
    tabIndex,
    addToExcel,
    whichTabData,
    isCameOptions,
    globalFilterValue,
    notification,
    setNotification,
    multiNotification,
    setMultiNotification,
    selectedRow,
    setSelectedRow,
    tabs,
    handleTabChange,
    hasItems,
    exportExcel,
    showExcel,
    onGlobalFilterChange,
    activeBlockList,
    activeFloorList,
    canceledBlockList,
    canceledFloorList,
  }
}
